import { GoogleMap, LoadScript } from '@react-google-maps/api';
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { salesApiTokenRequest } from '../auth/msalConfig';
import '../styles/SalesMap.css';

// Google Maps libraries required for AdvancedMarkerElement
const GOOGLE_MAPS_LIBRARIES: ("marker")[] = ['marker'];

const API_BASE_URL = import.meta.env.PROD ? '' : 'http://localhost:3001';

// UK centre for initial map view
const UK_CENTER = { lat: 53.0, lng: -1.5 };
const UK_ZOOM = 6;

/** Shape of each care home from the sales_search API */
interface SalesSearchCareHome {
    id: number;
    cqc_id: string;
    organisation_name: string;
    open_score: number;
    latitude: number;
    longitude: number;
    post_code: string;
    regulator: string;
    slug: string;
    freshness_days: number;
    is_premium: boolean;
    residential_needs: boolean;
    dementia_needs: boolean;
    nursing_needs: boolean;
    fees_start_from: number | undefined;
    Days_Since_Last_Inspected_CQC: number;
    self_funding_available: boolean;
    local_authority_available: boolean;
    top_up_available: boolean;
    chc_available: boolean;
    timeline_appearances: number;
}

interface SalesSearchResponse {
    success: boolean;
    search: string | null;
    results: SalesSearchCareHome[];
    meta: {
        totalCareHomes: number;
        returnedCount: number;
        filtered: boolean;
        cacheAge: number;
        queryDurationMs: number;
    };
}

/**
 * Creates a round map pin showing the timeline_appearances count.
 * Colour intensity scales with the count: grey for 0, deepening green for higher counts.
 */
function createAppearancePin(appearances: number): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.style.display = 'inline-block';
    wrapper.style.cursor = 'pointer';

    const size = 30;
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', size.toString());
    svg.setAttribute('height', size.toString());
    svg.setAttribute('viewBox', `0 0 ${size} ${size}`);

    // Choose colour based on appearance count
    let fill: string;
    if (appearances === 0) {
        fill = '#999999';
    } else if (appearances < 5) {
        fill = '#8dac8e';
    } else if (appearances < 20) {
        fill = '#5fb761';
    } else if (appearances < 50) {
        fill = '#00ba1d';
    } else {
        fill = '#025150';
    }

    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    const radius = (size / 2) - 2;
    circle.setAttribute('cx', (size / 2).toString());
    circle.setAttribute('cy', (size / 2).toString());
    circle.setAttribute('r', radius.toString());
    circle.setAttribute('fill', fill);
    circle.setAttribute('stroke', 'white');
    circle.setAttribute('stroke-width', '2.5');

    const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    text.setAttribute('x', (size / 2).toString());
    text.setAttribute('y', (size / 2 + 1).toString());
    text.setAttribute('text-anchor', 'middle');
    text.setAttribute('dominant-baseline', 'central');
    text.setAttribute('fill', 'white');
    text.setAttribute('font-size', appearances >= 100 ? '9' : '11');
    text.setAttribute('font-weight', 'bold');
    text.setAttribute('font-family', 'Arial, sans-serif');
    text.textContent = appearances.toString();

    svg.appendChild(circle);
    svg.appendChild(text);
    wrapper.appendChild(svg);

    return wrapper;
}

/**
 * SalesMap – interactive Google Map for salespeople showing all care homes
 * with their search-appearance statistics from the past 3 months.
 * Protected by Entra ID authentication.
 *
 * (update 2026-02-10.2)
 */
const SalesMap = () => {
    const { instance, accounts } = useMsal();
    const [careHomes, setCareHomes] = useState<SalesSearchCareHome[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchText, setSearchText] = useState('');
    const [resultCount, setResultCount] = useState(0);
    const [totalCount, setTotalCount] = useState(0);

    const mapRef = useRef<google.maps.Map | null>(null);
    const clustererRef = useRef<MarkerClusterer | null>(null);

    /** Acquire a Bearer token and call the sales_search API */
    const fetchCareHomes = useCallback(async (search: string) => {
        try {
            // Acquire access token
            let accessToken: string;
            try {
                const tokenResponse = await instance.acquireTokenSilent({
                    ...salesApiTokenRequest,
                    account: accounts[0],
                });
                accessToken = tokenResponse.accessToken;
            } catch (tokenError) {
                if (tokenError instanceof InteractionRequiredAuthError) {
                    await instance.acquireTokenRedirect(salesApiTokenRequest);
                    return;
                }
                throw tokenError;
            }

            const url = search.length >= 2
                ? `${API_BASE_URL}/api/sales_search?search=${encodeURIComponent(search)}`
                : `${API_BASE_URL}/api/sales_search`;

            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${accessToken}` },
            });

            if (!response.ok) {
                if (response.status === 401) {
                    setError('Authentication failed. Please sign in again.');
                } else {
                    setError('Failed to load care home data.');
                }
                return;
            }

            const data: SalesSearchResponse = await response.json();
            setCareHomes(data.results);
            setResultCount(data.meta.returnedCount);
            setTotalCount(data.meta.totalCareHomes);
            setError(null);
        } catch (err) {
            console.error('Sales map fetch error:', err);
            setError('Failed to connect to the server.');
        } finally {
            setLoading(false);
        }
    }, [instance, accounts]);

    // Initial load – fetch all care homes
    useEffect(() => {
        fetchCareHomes('');
    }, [fetchCareHomes]);

    // Execute search when user clicks the icon or presses Enter
    const executeSearch = useCallback(() => {
        const trimmed = searchText.trim();
        if (trimmed.length >= 2 || trimmed.length === 0) {
            setLoading(true);
            fetchCareHomes(trimmed);
        }
    }, [searchText, fetchCareHomes]);

    const handleSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            executeSearch();
        }
    }, [executeSearch]);

    // Place / update markers via MarkerClusterer whenever careHomes change
    useEffect(() => {
        if (!clustererRef.current || !window.google?.maps?.marker?.AdvancedMarkerElement) return;

        // Create markers without setting map -- the clusterer manages map assignment
        const newMarkers = careHomes
            .filter(h => h.latitude && h.longitude)
            .map(home => {
                const pin = createAppearancePin(home.timeline_appearances);
                const marker = new google.maps.marker.AdvancedMarkerElement({
                    position: { lat: home.latitude, lng: home.longitude },
                    content: pin,
                    title: `${home.organisation_name} (${home.timeline_appearances} appearances)`,
                });

                // Open sales_carehomes dashboard in a new window on pin click (update 2026-02-10.4)
                marker.addListener('click', () => {
                    window.open(
                        `${window.location.origin}/sales_carehomes?idcollection=${encodeURIComponent(home.cqc_id)}`,
                        '_blank'
                    );
                });

                return marker;
            });

        // Replace all markers in the clusterer
        clustererRef.current.clearMarkers();
        clustererRef.current.addMarkers(newMarkers);

    }, [careHomes]);

    const onMapLoad = useCallback((map: google.maps.Map) => {
        mapRef.current = map;
        // Create the clusterer once when the map loads
        clustererRef.current = new MarkerClusterer({
            map,
            algorithm: new SuperClusterAlgorithm({ radius: 80 }),
        });
    }, []);

    const onMapUnmount = useCallback(() => {
        if (clustererRef.current) {
            clustererRef.current.clearMarkers();
            clustererRef.current = null;
        }
        mapRef.current = null;
    }, []);

    if (error && careHomes.length === 0) {
        return <div className="sales-map-error">{error}</div>;
    }

    return (
        <div className="sales-map-container">
            {/* Search box overlay */}
            <div className="sales-map-search-box">
                <input
                    type="text"
                    placeholder="Search by name, postcode or CQC ID..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                />
                <button
                    className="sales-map-search-btn"
                    onClick={executeSearch}
                    title="Search"
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                </button>
            </div>

            {/* Status bar */}
            <div className="sales-map-status">
                {loading
                    ? 'Loading...'
                    : `Showing ${resultCount.toLocaleString()} of ${totalCount.toLocaleString()} care homes`}
            </div>

            {/* Google Map */}
            <LoadScript
                googleMapsApiKey="AIzaSyBI61DaE-XQ83wP1ORYfDTdANm56mdpL8M"
                libraries={GOOGLE_MAPS_LIBRARIES}
            >
                <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={UK_CENTER}
                    zoom={UK_ZOOM}
                    onLoad={onMapLoad}
                    onUnmount={onMapUnmount}
                    options={{
                        mapId: 'e67bf371a2993180f2d50a5f',
                        disableDefaultUI: false,
                        zoomControl: true,
                        streetViewControl: false,
                        mapTypeControl: false,
                        fullscreenControl: true,
                    }}
                />
            </LoadScript>
        </div>
    );
};

export default SalesMap;
